import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { Category } from 'components/EventCalendar/types';
import { FormInput } from 'components/Form/next';
import i18n from 'i18n';
import Joi from 'joi';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

interface Props {
    name: string;
}

export const pricesSchema = Joi.array()
    .items(
        Joi.object({
            category: Joi.string().required().label(i18n.t('bookings:pricesAssistant.fields.category')),
            price: Joi.number().required().min(0).label(i18n.t('bookings:pricesAssistant.fields.price')),
            units: Joi.number().required().min(0).label(i18n.t('bookings:pricesAssistant.fields.units')),
            hidden: Joi.boolean().label(i18n.t('bookings:pricesAssistant.fields.hidden'))
        }).unknown()
    )
    .messages({
        'array.min': `***${i18n.t('bookings:pricesAssistant.minPriceError')}`
    });

export const PricesAssistant = ({ name }: Props) => {
    const { t } = useTranslation('bookings');
    const clubSlug = useClubSlug();
    const { control, getFieldState, formState } = useFormContext();
    const { fields, append, remove } = useFieldArray({ control, name });
    const { error } = getFieldState(name, formState);
    const { data: categoryData } = useSWR<{ data: Category[] }>(`club-member/${clubSlug}/category`);

    const priceCategories = useMemo(
        () => [
            { key: 'default', label: t('pricesAssistant.categories.all') },
            { key: 'member', label: t('pricesAssistant.categories.members') },
            { key: 'relative', label: t('pricesAssistant.categories.responsible') },
            ...(categoryData?.data || []).map((cat) => ({
                key: `category:${cat.id}`,
                label: t('pricesAssistant.categories.categoryMembers', { title: cat.title })
            }))
        ],
        [categoryData, t]
    );

    return (
        <div className="tw-my-8">
            <Button
                className="tw-mb-0 tw-w-full tw-py-3"
                color="success"
                size="sm"
                onPress={() =>
                    append({
                        category: 'default',
                        price: 99.99,
                        units: 1
                    })
                }
            >
                {t('pricesAssistant.addPrice')}
            </Button>

            <div className="tw-my-2 tw-flex tw-flex-col tw-gap-2">
                {fields.map((field, index) => (
                    <div
                        key={field.id}
                        className="tw-grid tw-grid-cols-[repeat(3,auto)_minmax(auto,max-content)] tw-items-center tw-gap-4"
                    >
                        <FormInput.Select
                            name={`${name}.${index}.category`}
                            options={priceCategories}
                            className="tw-col-span-1"
                        />

                        <FormInput
                            flush
                            type="number"
                            name={`${name}.${index}.units`}
                            labelRight="U"
                            className="tw-col-span-2 tw-mt-1 md:tw-col-span-1"
                        />

                        <FormInput
                            flush
                            type="number"
                            step={0.01}
                            name={`${name}.${index}.price`}
                            labelRight="EUR"
                            className="tw-col-span-1 tw-mt-1"
                        />

                        <div className="tw-col-span-4 tw-flex tw-items-center tw-justify-center tw-gap-4 md:tw-col-span-1">
                            <FormInput.Switch name={`${name}.${index}.hidden`} />

                            <Button type="button" color="error" onPress={() => remove(index)}>
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
            {error && <small className="tw-text-error">{error?.message}</small>}
        </div>
    );
};
